/* eslint-disable no-nested-ternary */
import { Paragraph } from '@tmov/design-system';
import {
  addHours,
  format,
  formatDistance,
  intervalToDuration,
  isAfter,
} from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import {
  formatarMilhar,
  formatarMoeda,
  toTitleCase,
} from '../../../../helpers/formatador';
import StatusCotacaoEnum from '../../Enum/StatusCotacaoEnum';
import {
  CotacaoContainer,
  DetailsContainer,
  LogoContainer,
  Separator,
  TimeContainer,
} from './styles';

const Cotacao = ({ cotacao, status, ...props }) => {
  const timeStatus = useMemo(() => {
    if (status === StatusCotacaoEnum.AGENDADO) {
      return 'Agendado';
    }

    if (status === StatusCotacaoEnum.ENCERRADO) {
      return 'Encerrado';
    }

    return 'Tempo restante';
  }, [status]);

  const remainingTime = useMemo(() => {
    const now = new Date();
    const periodoInicio = new Date(cotacao.periodoInicio);
    const periodoFim = new Date(cotacao.periodoFim);

    const periodoFimAtualizado = addHours(periodoFim, 3);

    if (status === StatusCotacaoEnum.AGENDADO) {
      return `Inicia em ${formatDistance(now, addHours(periodoInicio, 3), {
        locale: ptBR,
      })}`;
    }

    if (status === StatusCotacaoEnum.ENCERRADO) {
      return `${format(addHours(periodoFim, 3), 'dd/MM/yyyy')} às ${format(
        addHours(periodoFim, 3),
        'HH:mm'
      )}`;
    }

    const duration = intervalToDuration({
      start: now,
      end: periodoFimAtualizado,
    });
    const minutes = duration.minutes.toFixed(0);

    const milliseconds = Math.abs(
      periodoFimAtualizado.getTime() - now.getTime()
    );
    const hours = (milliseconds / 1000 / 60 / 60).toFixed(0);
    const days = (hours / 24).toFixed(0);

    const daysCounter = hours < 24 ? '' : ` (${days} dias)`;

    const past = isAfter(now, periodoFimAtualizado);
    if (past) return 'OO:OO';

    return `${hours
      .toString()
      .padStart(2, '0')} : ${minutes
      .toString()
      .padStart(2, '0')}${daysCounter}`;
  }, [cotacao.periodoFim, cotacao.periodoInicio, status]);

  return (
    <CotacaoContainer {...props}>
      <LogoContainer>
        <img
          src={`https://static.sotran.com.br/${cotacao.logoUrl}`}
          alt="Cliente Logo Marca"
        />
      </LogoContainer>
      <TimeContainer>
        <Paragraph variant="p2" style={{ margin: 0 }}>
          {timeStatus}
        </Paragraph>
        <Paragraph variant="p1" fontWeight="semibold" style={{ margin: 0 }}>
          {remainingTime}
        </Paragraph>
      </TimeContainer>
      <Separator />
      <DetailsContainer>
        <Paragraph variant="p2" style={{ textAlign: 'left' }}>
          <>
            <span>Melhor lance - </span>
            {cotacao.melhorLance ? formatarMoeda(cotacao.melhorLance) : 'N.A.'}
          </>
        </Paragraph>
        <Paragraph variant="p2" style={{ textAlign: 'left' }}>
          <>
            <span>Origem - </span>
            {cotacao.cidadeOrigem
              ? `${toTitleCase(cotacao.cidadeOrigem)} (${cotacao.estadoOrigem})`
              : 'N.A.'}
          </>
        </Paragraph>
        <Paragraph variant="p2" style={{ textAlign: 'left' }}>
          <>
            <span>Destino - </span>
            {cotacao.cidadeDestino
              ? `${toTitleCase(cotacao.cidadeDestino)} (${
                  cotacao.estadoDestino
                })`
              : 'N.A.'}
          </>
        </Paragraph>
        <Paragraph variant="p2" style={{ textAlign: 'left' }}>
          <>
            <span>Produto - </span>
            {cotacao.produto ? toTitleCase(cotacao.produto) : 'N.A.'}
          </>
        </Paragraph>
        <Paragraph variant="p2" style={{ textAlign: 'left' }}>
          <>
            <span>Volume - </span>
            {`${formatarMilhar(cotacao.volume)} ton` ?? 'N.A.'}
          </>
        </Paragraph>
      </DetailsContainer>
    </CotacaoContainer>
  );
};

Cotacao.propTypes = {
  status: PropTypes.number.isRequired,
  cotacao: PropTypes.shape({
    id: PropTypes.number,
    logoUrl: PropTypes.string,
    periodoInicio: PropTypes.string,
    periodoFim: PropTypes.string,
    melhorLance: PropTypes.number,
    cidadeOrigem: PropTypes.string,
    cidadeDestino: PropTypes.string,
    estadoOrigem: PropTypes.string,
    estadoDestino: PropTypes.string,
    produto: PropTypes.string,
    volume: PropTypes.number,
  }).isRequired,
};

export { Cotacao };
